/**
 * ICF Progress API Service
 * 
 * Secure API client for ICF progress tracking with:
 * - Multi-tenant isolation
 * - Request/response validation
 * - Error handling and retry logic
 * - Authentication integration
 */
import { apiRequest } from './apiClient';
import { derivedIdempotencyKey } from '../idempotencyIntent';
import { noCreditErrorKey } from '../../utils/creditErrors';
import api from '../api';
import {
  ICFProgressResponse,
  ICFGoalResponse,
  ICFGoalUpdateRequest,
  ProgressDashboardResponse,
  ProgressDashboardPayload,
  SessionAnalysisResponse,
  CompetencyHistoryResponse,
  TrackingPreferencesRequest,
  CompetencyDetailResponse,
  EvidenceFilterParams,
  EvidenceResponse,
  PracticeExamAnswer,
  PracticeExamAttemptResponse,
  PracticeExamQuestionsResponse,
} from '../../types/icfProgress';

const isDevRuntime = process.env.NODE_ENV !== 'production';
// Screenshot mode serves a local mock dashboard. Dev builds only (FE-11): the
// __DEV__ check comes first so production builds never use the mock, and the
// mock module is loaded with a dynamic import inside the gated branch so it is
// not part of the production bundle's main chunk.
const isScreenshotModeEnabled = () =>
  __DEV__ && process.env.EXPO_PUBLIC_ICF_SCREENSHOT_MODE === 'true';

export function isIcfProgressAccessDeniedError(error: unknown): boolean {
  const message =
    typeof error === 'string'
      ? error
      : error instanceof Error
        ? error.message
        : typeof (error as any)?.message === 'string'
          ? (error as any).message
          : '';

  return /icf progress is available on tier2|tier2\+|forbidden|access denied|http 403/i.test(message);
}

export class ICFProgressAPI {
  private static readonly BASE_PATH = '/icf-progress';
  private static dashboardCache: {
    promise: Promise<ProgressDashboardPayload> | null;
    data: ProgressDashboardPayload | null;
    timestamp: number;
  } = {
      promise: null,
      data: null,
      timestamp: 0,
    };
  private static cooldownUntil = 0;
  private static cacheGeneration = 0;

  /**
   * Get user's progress dashboard with all data
   */
  static async getDashboard(options: {
    bypassCache?: boolean;
    cacheMs?: number;
    allowTeaser: true;
  }): Promise<ProgressDashboardPayload>;
  static async getDashboard(options?: {
    bypassCache?: boolean;
    cacheMs?: number;
    allowTeaser?: false;
  }): Promise<ProgressDashboardResponse>;
  static async getDashboard(options: {
    bypassCache?: boolean;
    cacheMs?: number;
    allowTeaser?: boolean;
  } = {}): Promise<ProgressDashboardPayload> {
    const { bypassCache = false, cacheMs = 15000 } = options;

    const now = Date.now();
    const state = this.dashboardCache;

    const isDev = isDevRuntime;
    if (isDev) {
      console.debug('[ICFProgressAPI] getDashboard invoked', {
        bypassCache,
        cacheMs,
        hasData: Boolean(state.data),
        hasPromise: Boolean(state.promise),
        ageMs: state.timestamp ? now - state.timestamp : null,
        cooldownRemaining: this.cooldownUntil > now ? this.cooldownUntil - now : 0,
        stack: new Error().stack,
      });
    }

    if (__DEV__ && isScreenshotModeEnabled()) {
      const { buildMockIcfDashboard } = await import('../../mocks/mockIcfDashboard');
      const mockResponse = buildMockIcfDashboard();
      this.cooldownUntil = 0;
      this.dashboardCache = {
        promise: null,
        data: mockResponse,
        timestamp: now,
      };
      if (isDev) {
        console.debug('[ICFProgressAPI] returning local mock dashboard (screenshot mode)');
      }
      return mockResponse;
    }

    if (!bypassCache && this.cooldownUntil > now) {
      if (state.data) {
        if (isDev) {
          console.debug('[ICFProgressAPI] returning cached dashboard due to cooldown');
        }
        return state.data;
      }
      if (isDev) {
        console.debug('[ICFProgressAPI] skipping fetch due to cooldown');
      }
      throw new Error('Rate limit active, please try again shortly');
    }

    if (!bypassCache && state.data && now - state.timestamp < cacheMs) {
      if (isDev) {
        console.debug('[ICFProgressAPI] returning cached dashboard');
      }
      return state.data;
    }

    if (state.promise) {
      if (isDev) {
        console.debug('[ICFProgressAPI] reusing in-flight dashboard promise');
      }
      return state.promise;
    }

    const requestGeneration = this.cacheGeneration;
    state.promise = (async () => {
      try {
        if (isDev) {
          console.debug('[ICFProgressAPI] fetching dashboard from API');
        }
        const response = await apiRequest<ProgressDashboardPayload>({
          endpoint: `${this.BASE_PATH}/dashboard`,
          method: 'GET',
        });

        // An auth transition may invalidate the cache while this request is
        // in flight. Never let the previous user's response repopulate it.
        if (requestGeneration === this.cacheGeneration) {
          this.dashboardCache = {
            promise: null,
            data: response,
            timestamp: Date.now(),
          };
        }

        return response;
      } catch (error: any) {
        if (requestGeneration === this.cacheGeneration) {
          this.dashboardCache.promise = null;
        }
        if (typeof error?.message === 'string' && (/429/.test(error.message) || /rate limit/i.test(error.message))) {
          this.cooldownUntil = Date.now() + cacheMs;
        }
        console.error('Failed to fetch progress dashboard:', error);
        if (isDev) {
          console.debug('[ICFProgressAPI] dashboard fetch error details', {
            message: error?.message,
            stack: error?.stack,
          });
        }
        throw new Error(error?.message || 'Dashboard verilerinizi yükleyemedik');
      }
    })();

    return state.promise;
  }

  /** Clear all user-scoped progress state when the authenticated identity changes. */
  static invalidateDashboardCache() {
    this.cacheGeneration += 1;
    this.dashboardCache = {
      promise: null,
      data: null,
      timestamp: 0,
    };
    this.cooldownUntil = 0;
  }

  static async updateGoal(goal: ICFGoalUpdateRequest): Promise<ICFGoalResponse> {
    try {
      const response = await apiRequest<ICFGoalResponse>({
        endpoint: `${this.BASE_PATH}/goal`,
        method: 'PUT',
        data: goal,
      });
      this.invalidateDashboardCache();
      return response;
    } catch (error: any) {
      console.error('Failed to update ICF goal:', error);
      throw new Error(error.message || 'Hedef güncellenemedi');
    }
  }

  static async exportEvidencePortfolioPdf(language: 'en' | 'tr' = 'en'): Promise<Blob> {
    const response = await api.get(`/api${this.BASE_PATH}/portfolio/pdf`, {
      params: { language },
      responseType: 'blob',
    });
    const blob = response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: 'application/pdf' });
    if (blob.size === 0) {
      throw new Error('Empty PDF response');
    }
    return blob.type === 'application/pdf'
      ? blob
      : new Blob([blob], { type: 'application/pdf' });
  }

  static async getPracticeExamQuestions(): Promise<PracticeExamQuestionsResponse> {
    return apiRequest<PracticeExamQuestionsResponse>({
      endpoint: `${this.BASE_PATH}/practice-exam/questions`,
      method: 'GET',
    });
  }

  /**
   * One attempt per `idempotencyKey`: a retried POST of the same attempt
   * returns the first result and is not counted again (server dedup).
   */
  static async submitPracticeExam(
    answers: PracticeExamAnswer[],
    options: { idempotencyKey?: string } = {},
  ): Promise<PracticeExamAttemptResponse> {
    return apiRequest<PracticeExamAttemptResponse, { answers: PracticeExamAnswer[] }>({
      endpoint: `${this.BASE_PATH}/practice-exam/attempts`,
      method: 'POST',
      data: { answers },
      ...(options.idempotencyKey ? { headers: { 'Idempotency-Key': options.idempotencyKey } } : {}),
    });
  }

  /**
   * Get user's ICF progress data
   */
  static async getProgress(): Promise<ICFProgressResponse> {
    try {
      const response = await apiRequest<ICFProgressResponse>({
        endpoint: this.BASE_PATH,
        method: 'GET',
      });

      return response;
    } catch (error: any) {
      console.error('Failed to fetch ICF progress:', error);
      throw new Error(error.message || 'İlerleme verilerinizi yükleyemedik');
    }
  }

  static async getCompetencyDetail(
    competency: string,
    language: 'en' | 'tr' = 'tr',
  ): Promise<CompetencyDetailResponse> {
    return apiRequest<CompetencyDetailResponse>({
      endpoint: `${this.BASE_PATH}/competency/${encodeURIComponent(competency)}/detail?language=${language}`,
      method: 'GET',
    });
  }

  /**
   * Update tracking preferences
   */
  static async updatePreferences(
    preferences: TrackingPreferencesRequest
  ): Promise<ICFProgressResponse> {
    try {
      // Validate preferences
      if (preferences.data_retention_days !== undefined) {
        if (preferences.data_retention_days < 1 || preferences.data_retention_days > 2555) {
          throw new Error('Veri saklama süresi 1-2555 gün arasında olmalıdır');
        }
      }

      const response = await apiRequest<ICFProgressResponse>({
        endpoint: `${this.BASE_PATH}/preferences`,
        method: 'PUT',
        data: preferences,
      });

      return response;
    } catch (error: any) {
      console.error('Failed to update tracking preferences:', error);
      throw new Error(error.message || 'Tercihlerinizi güncelleyemedik');
    }
  }

  /**
   * Get session analyses with pagination
   */
  static async getSessionAnalyses(params?: {
    limit?: number;
    offset?: number;
    include_excluded?: boolean;
  }): Promise<SessionAnalysisResponse[]> {
    try {
      const queryParams = new URLSearchParams();

      if (params?.limit) queryParams.set('limit', params.limit.toString());
      if (params?.offset) queryParams.set('offset', params.offset.toString());
      if (params?.include_excluded !== undefined) {
        queryParams.set('include_excluded', params.include_excluded.toString());
      }

      const response = await apiRequest<SessionAnalysisResponse[]>({
        endpoint: `${this.BASE_PATH}/sessions?${queryParams.toString()}`,
        method: 'GET',
      });

      return response;
    } catch (error: any) {
      console.error('Failed to fetch session analyses:', error);
      throw new Error(error.message || 'Oturum analizlerini yükleyemedik');
    }
  }

  /**
   * Get a single session analysis detail
   */
  static async getSessionDetail(sessionId: number): Promise<SessionAnalysisResponse> {
    try {
      if (!Number.isInteger(sessionId) || sessionId <= 0) {
        throw new Error('Geçersiz oturum ID');
      }
      const response = await apiRequest<SessionAnalysisResponse>({
        endpoint: `${this.BASE_PATH}/sessions/${sessionId}`,
        method: 'GET',
      });
      return response;
    } catch (error: any) {
      console.error('Failed to fetch session detail:', error);
      throw new Error(error.message || 'Oturum detayını yükleyemedik');
    }
  }

  /**
   * Toggle session inclusion in analysis
   */
  static async toggleSessionAnalysis(
    sessionId: number,
    includeInAnalysis: boolean
  ): Promise<{ success: boolean; session_id: number; include_in_analysis: boolean }> {
    try {
      if (!Number.isInteger(sessionId) || sessionId <= 0) {
        throw new Error('Geçersiz oturum ID');
      }

      const response = await apiRequest<{
        success: boolean;
        session_id: number;
        include_in_analysis: boolean;
      }>({
        endpoint: `${this.BASE_PATH}/sessions/${sessionId}/toggle?include_in_analysis=${includeInAnalysis}`,
        method: 'POST',
      });

      return response;
    } catch (error: any) {
      console.error('Failed to toggle session analysis:', error);
      throw new Error(error.message || 'Oturum ayarını güncelleyemedik');
    }
  }

  /**
   * Re-run a stored transcript through the analysis pipeline, optionally at an
   * explicit ICF level so the session produces the official instrument ratings
   * the CRF readiness rule needs (`POST /icf-progress/analyze/session/{id}`).
   *
   * ASYNC-6: pass one `idempotencyKey` per user intent. A repeated request with
   * the same key returns the re-run it already started (`replayed: true`, in
   * whatever state it is in) instead of spending a free re-run or a credit.
   */
  static async analyzeSession(
    sessionId: number,
    requestedLevel?: 'acc' | 'pcc' | 'mcc',
    options: { idempotencyKey?: string } = {},
  ): Promise<{ task_id?: string; status?: string; replayed?: boolean }> {
    if (!Number.isInteger(sessionId) || sessionId <= 0) {
      throw new Error('Invalid session id');
    }
    const query = requestedLevel ? `?requested_level=${requestedLevel}` : '';
    return apiRequest<{ task_id?: string; status?: string; replayed?: boolean }>({
      endpoint: `${this.BASE_PATH}/analyze/session/${sessionId}${query}`,
      method: 'POST',
      ...(options.idempotencyKey ? { headers: { 'Idempotency-Key': options.idempotencyKey } } : {}),
    });
  }

  /**
   * Fan out `analyzeSession` over the sessions that have no instrument result at
   * the goal level. There is no bulk endpoint today; failures are reported per
   * session so one bad transcript does not abort the rest.
   *
   * With an `idempotencyKey` each session gets its own key derived from it, so
   * re-clicking the same request (or retrying after some sessions failed)
   * starts only the sessions that have no run yet. `settled` counts sessions
   * whose key replayed a run that already finished: the caller should start a
   * new intent (fresh key) for its next attempt.
   */
  static async analyzeSessionsAtLevel(
    sessionIds: number[],
    requestedLevel: 'acc' | 'pcc' | 'mcc',
    idempotencyKey?: string,
  ): Promise<{
    started: number;
    failed: number;
    settled: number;
    noCredits: number;
    noCreditKey: string | null;
  }> {
    const results = await Promise.allSettled(
      sessionIds.map((sessionId) => this.analyzeSession(sessionId, requestedLevel, {
        idempotencyKey: idempotencyKey
          ? derivedIdempotencyKey(idempotencyKey, `session-${sessionId}`)
          : undefined,
      })),
    );
    const started = results.filter((item) => item.status === 'fulfilled').length;
    const settled = results.filter(
      (item) => item.status === 'fulfilled' && item.value?.replayed && item.value.status !== 'processing',
    ).length;
    // Metered re-runs refused for lack of credit (402 `no_credits`, or
    // `subscription_required` for a lapsed plan). `noCreditKey` is the copy
    // for the first refusal, so a lapsed user is told to choose a plan.
    const noCreditKeys = results
      .map((item) => (item.status === 'rejected' ? noCreditErrorKey(item.reason) : null))
      .filter((key): key is string => key !== null);
    return {
      started,
      failed: results.length - started,
      settled,
      noCredits: noCreditKeys.length,
      noCreditKey: noCreditKeys[0] ?? null,
    };
  }

  /**
   * Get evidence quotes captured for a single analyzed session
   */
  static async getSessionEvidence(
    sessionId: number,
    params?: EvidenceFilterParams,
  ): Promise<EvidenceResponse> {
    if (!Number.isInteger(sessionId) || sessionId <= 0) {
      throw new Error('Invalid session id');
    }

    const queryParams = new URLSearchParams();
    if (params?.competency) queryParams.set('competency', params.competency);
    if (params?.marker_id) queryParams.set('marker_id', params.marker_id);
    if (params?.evidence_type) queryParams.set('evidence_type', params.evidence_type);
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.page_size) queryParams.set('page_size', params.page_size.toString());

    const query = queryParams.toString();
    return apiRequest<EvidenceResponse>({
      endpoint: `${this.BASE_PATH}/evidence/${sessionId}${query ? `?${query}` : ''}`,
      method: 'GET',
    });
  }

  /**
   * Purge all user progress data (GDPR compliance)
   */
  static async purgeProgressData(): Promise<{ message: string; success: boolean }> {
    try {
      const response = await apiRequest<{ message: string; success: boolean }>({
        endpoint: `${this.BASE_PATH}/data/purge?confirm=true`,
        method: 'DELETE',
      });

      return response;
    } catch (error: any) {
      console.error('Failed to purge progress data:', error);
      throw new Error(error.message || 'Veri silme işlemi başarısız oldu');
    }
  }

  static async getStudentDashboard(studentId: number): Promise<ProgressDashboardResponse> {
    try {
      if (!Number.isInteger(studentId) || studentId <= 0) {
        throw new Error('Geçersiz öğrenci ID');
      }
      const response = await apiRequest<ProgressDashboardResponse>({
        endpoint: `${this.BASE_PATH}/admin/students/${studentId}/dashboard`,
        method: 'GET',
      });
      return response;
    } catch (error: any) {
      console.error('Failed to fetch student dashboard:', error);
      throw new Error(error.message || 'Öğrenci panelini yükleyemedik');
    }
  }

  // Utility Methods

  /**
   * Validate competency name
   */
  static isValidCompetency(competency: string): boolean {
    const validCompetencies = [
      'demonstrates_ethical_practice',
      'embodies_coaching_mindset',
      'establishes_maintains_agreements',
      'cultivates_trust_safety',
      'maintains_presence',
      'listens_actively',
      'evokes_awareness',
      'facilitates_client_growth'
    ];

    return validCompetencies.includes(competency);
  }

  /**
   * Get competency display name in Turkish
   */
  static getCompetencyDisplayName(competency: string): string {
    const names: Record<string, string> = {
      'demonstrates_ethical_practice': 'Etik Kuralları Uygular',
      'embodies_coaching_mindset': 'Koçluk Zihin Yapısını İçselleştirir',
      'establishes_maintains_agreements': 'Anlaşmaları Oluşturur ve Sürdürür',
      'cultivates_trust_safety': 'İtimat ve Güven Oluşturur',
      'maintains_presence': 'Koç Konumunu Sürdürür',
      'listens_actively': 'Etkin Dinler',
      'evokes_awareness': 'Farkındalık Yaratır',
      'facilitates_client_growth': 'Müşterinin Gelişimini Kolaylaştırır'
    };

    return names[competency] || competency;
  }

  /**
   * Format progress score with color coding
   */
  static getScoreColor(score: number): string {
    if (score >= 80) return '#4CAF50'; // Green
    if (score >= 60) return '#FF9800'; // Orange
    if (score >= 40) return '#FF5722'; // Deep Orange
    return '#F44336'; // Red
  }

  /**
   * Get score level text
   */
  static getScoreLevel(score: number): string {
    if (score >= 80) return 'İleri Seviye';
    if (score >= 60) return 'Orta Seviye';
    if (score >= 40) return 'Temel Seviye';
    return 'Gelişim Gerekli';
  }
}
